import { useState, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import { Card, Button, Input, SearchBar, ConfirmDialog, Modal } from '../../../shared/components/UI'
import { shadesApi } from '../../../shared/lib/api'
import { Trash2, PencilLine, Palette } from 'lucide-react'

const PRESETS = [
  '#D4AF37', '#C5A059', '#B8860B', '#F5F5DC',
  '#C0C0C0', '#A9A9A9', '#36454F', '#A52A2A',
  '#CD853F', '#BC8F8F', '#8B0000', '#800000',
  '#E0115F', '#008000', '#008080', '#002366',
  '#000080', '#20B2AA', '#800080'
]

const emptyForm = { name: '', code: '', status: 'active', color: '#F59E0B', type: 'jari' }

export default function ShadeMastersPage() {
  const [shades, setShades] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const colorInputRef = useRef(null)

  useEffect(() => { loadShades() }, [])

  const loadShades = () => {
    setLoading(true)
    shadesApi.list()
      .then((res) => setShades(res.data.data))
      .finally(() => setLoading(false))
  }

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const visible = shades.filter((s) => 
    (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.code || '').toLowerCase().includes(search.toLowerCase())
  )

  const startNew = () => { setEditing('new'); setForm(emptyForm) }
  const startEdit = (s) => {
    setEditing(s.id)
    setForm({
      name: s.name,
      code: s.code,
      status: s.status,
      color: s.color || '#F59E0B',
      type: s.type || 'jari'
    })
  }

  const cancel = () => { setEditing(null); setForm(emptyForm) }

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) return
    setIsSaving(true)
    try {
      if (editing === 'new') {
        await shadesApi.create(form)
      } else {
        await shadesApi.update(editing, form)
      }
      loadShades()
      cancel()
    } catch (err) {
      console.error('Failed to save shade:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const toggleStatus = async (s) => {
    const newStatus = s.status === 'active' ? 'inactive' : 'active'
    try {
      await shadesApi.update(s.id, { status: newStatus })
      setShades((p) => p.map((x) => x.id === s.id ? { ...x, status: newStatus } : x))
    } catch (err) {
      console.error('Failed to toggle shade status:', err)
    }
  }

  const handleDelete = (s) => {
    setDeleteError('')
    setConfirmDelete(s)
  }

  const performDelete = async () => {
    if (!confirmDelete) return
    setIsDeleting(true)
    try {
      await shadesApi.delete(confirmDelete.id)
      setShades((p) => p.filter((x) => x.id !== confirmDelete.id))
      setConfirmDelete(null)
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete shade.')
    } finally {
      setIsDeleting(false)
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading shades...</div>

  return (
    <div className="space-y-4">
      <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search shades...">
        <Button onClick={startNew}>+ Add Shade</Button>
      </SearchBar>

      <Card bodyClass="!p-0 overflow-hidden rounded-2xl shadow-sm border-gray-100">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50/50">
              <th>Preview</th>
              <th>Shade Name</th>
              <th>Inventory Code</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-20 text-gray-400">No shades found</td></tr>
            ) : (
              visible.map((s) => (
                <tr key={s.id} className={clsx('group hover:bg-gray-50/30 transition-colors', s.status === 'inactive' && 'opacity-40')}>
                  <td className="py-4">
                    <div className="w-10 h-10 rounded-xl border-2 border-white shadow-sm ring-1 ring-gray-100" style={{ backgroundColor: s.color }} />
                  </td>
                  <td>
                    <span className="font-bold text-gray-800">{s.name}</span>
                  </td>
                  <td className="font-mono text-xs text-brand-600 font-bold">{s.code}</td>
                  <td>
                    <button onClick={() => toggleStatus(s)}
                      className={clsx(
                        'text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border transition-all',
                        s.status === 'active' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-gray-100 text-gray-400 border-gray-100'
                      )}>
                      {s.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="pr-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => startEdit(s)} className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg"><PencilLine size={18} /></button>
                      <button onClick={() => handleDelete(s)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {editing && (
        <Modal
          isOpen={!!editing}
          onClose={cancel}
          title={editing === 'new' ? 'Create New Shade' : 'Update Shade'}
          size="md"
          confirmClose={form.name.trim() !== ''}
        >
          <div className="space-y-6 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="space-y-4 pr-1">
              <Input 
                label="Shade Name" 
                value={form.name} 
                onChange={f('name')} 
                placeholder="e.g. Royal Gold"
                className="rounded-xl"
              />
              <Input 
                label="Inventory Code" 
                value={form.code} 
                onChange={f('code')} 
                placeholder="e.g. #22K"
                className="rounded-xl font-mono"
              />
            </div>

            {/* Type Toggle */}
            <div className="bg-gray-50/50 p-1 rounded-2xl flex border border-gray-100">
              <button onClick={() => setForm(p => ({ ...p, type: 'jari' }))}
                className={clsx('flex-1 py-2.5 rounded-xl text-sm font-bold transition-all', form.type === 'jari' ? 'bg-white text-brand-600 shadow-sm border border-gray-100' : 'text-gray-400')}>
                Jari Color
              </button>
              <button onClick={() => setForm(p => ({ ...p, type: 'other' }))}
                className={clsx('flex-1 py-2.5 rounded-xl text-sm font-bold transition-all', form.type === 'other' ? 'bg-white text-brand-600 shadow-sm border border-gray-100' : 'text-gray-400')}>
                Other
              </button>
            </div>

            <div className="space-y-3 flex-1 overflow-hidden">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Visual Identification</p>
              <div className="p-5 bg-gray-50/30 border border-gray-100 rounded-3xl space-y-6">
                
                {/* Custom Picker */}
                <div className="flex items-center gap-4">
                  <div 
                    className="relative w-16 h-16 rounded-[22px] border-4 border-white shadow-xl ring-1 ring-gray-100 flex items-center justify-center text-white/50 overflow-hidden cursor-pointer active:scale-95 transition-transform"
                    style={{ backgroundColor: form.color }}
                    onClick={() => colorInputRef.current?.click()}
                  >
                    <Palette size={24} />
                    <input ref={colorInputRef} type="color" value={form.color} onChange={f('color')} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Pick Custom Color</p>
                    <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-brand-500/20 transition-all">
                      <span className="px-3 text-gray-300 font-mono">#</span>
                      <input value={form.color.replace('#', '')} onChange={(e) => setForm(p => ({ ...p, color: `#${e.target.value}` }))}
                        className="w-full py-2.5 outline-none font-mono text-sm font-bold text-gray-700 uppercase" placeholder="F59E0B" />
                    </div>
                  </div>
                </div>

                {/* Smart Presets */}
                <div className="space-y-3">
                  <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Smart Presets</p>
                  <div className="grid grid-cols-4 gap-3">
                    {PRESETS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setForm(p => ({ ...p, color: c }))}
                        className={clsx(
                          'h-10 rounded-full border-2 transition-all hover:scale-105 active:scale-95 relative overflow-hidden',
                          form.color === c ? 'border-brand-500 ring-4 ring-brand-500/10' : 'border-white shadow-sm'
                        )}
                        style={{ 
                          backgroundColor: c,
                          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)'
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button 
                onClick={save} 
                disabled={isSaving || !form.name.trim() || !form.code.trim()} 
                className={clsx(
                  "w-full h-14 rounded-2xl text-white font-bold shadow-lg transition-all active:scale-95",
                  (!form.name.trim() || !form.code.trim()) ? "bg-slate-400" : "bg-brand-600 shadow-brand-200"
                )}
              >
                {isSaving ? 'Saving...' : (editing === 'new' ? 'Add Shade' : 'Update Shade')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={performDelete} isLoading={isDeleting}
        title="Delete Shade Master?" message={`Delete "${confirmDelete?.name}"?`} variant="danger" error={deleteError} />
    </div>
  )
}
